import Alert from '@amzn/awsui-components-core/alert';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import FormField from '@amzn/awsui-components-core/form-field';
import Input from '@amzn/awsui-components-core/input';
import Modal from '@amzn/awsui-components-core/modal';
import SegmentedControl from '@amzn/awsui-components-core/segmented-control';
import Select from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import React from 'react';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import type { NavPreferenceScope } from 'src/components/star-grid/hooks/useStarGridNavPreference';
import type { NavigationReport } from 'src/components/star-grid/navigation.types';
import { CUSTOM_NAV_GROUP_PREFIX } from 'src/components/star-grid/utils/starGridNavGroups';
import type { StarGridNavAssignment, StarGridNavGroupOption } from 'src/components/star-grid/utils/starGridNavGroups';
import styles from './styles/OrganizeReportsModal.module.scss';

export interface OrganizeReportsModalProps {
  readonly visible: boolean;
  readonly assignments: readonly StarGridNavAssignment[];
  readonly groupOptions: readonly StarGridNavGroupOption[];
  readonly newGroupName: string;
  readonly canAddGroup: boolean;
  readonly canReset: boolean;
  /** Admins can switch between their own layout and the default every user starts from. */
  readonly canEditDefault?: boolean;
  readonly scope?: NavPreferenceScope;
  readonly onScopeChange?: (scope: NavPreferenceScope) => void;
  /** Set when the last change did not reach the service. The arrangement on screen still stands. */
  readonly saveError?: string;
  readonly onNewGroupNameChange: (value: string) => void;
  readonly onAddGroup: () => void;
  readonly onMoveSection: (groupId: string, offset: number) => void;
  /** Deletes a section the user added; its reports return to their default sections. */
  readonly onRemoveGroup?: (groupId: string) => void;
  readonly onReportGroupChange: (report: NavigationReport, groupId: string) => void;
  readonly onReset: () => void;
  readonly onDismiss: () => void;
}

/**
 * Rearranges the groups INSIDE the Reports nav row: a section order list, a section picker per report,
 * and a way to add a section of the user's own. Opened from the organize control on that row.
 *
 * A picker rather than drag and drop. Reassigning a report is a one-in-many CHOICE, which a select
 * states directly and keyboard users can reach; dragging a row from one long collapsible section
 * into another needs auto-scroll, drop targets in collapsed sections, and a keyboard equivalent
 * anyway. Section ORDER is short and genuinely positional, so it gets up/down buttons.
 */
export const OrganizeReportsModal: React.FC<OrganizeReportsModalProps> = ({
  visible,
  assignments,
  groupOptions,
  newGroupName,
  canAddGroup,
  canReset,
  canEditDefault = false,
  scope = 'mine',
  onScopeChange,
  saveError,
  onNewGroupNameChange,
  onAddGroup,
  onMoveSection,
  onRemoveGroup,
  onReportGroupChange,
  onReset,
  onDismiss
}) => {
  const text = useStarGridText();
  const options = groupOptions.map((option) => ({ value: option.value, label: option.label }));
  return (
    <Modal
      data-testid="stargrid-nav-organize-modal"
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header={text('navOrganizeReports')}
      closeAriaLabel={text('navOrganizeClose')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button disabled={!canReset} onClick={onReset} data-testid="stargrid-nav-reset-groups">
              {text(scope === 'default' ? 'navResetDefault' : 'navResetGroups')}
            </Button>
            <Button variant="primary" onClick={onDismiss} data-testid="stargrid-nav-organize-done">
              {text('navOrganizeClose')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {canEditDefault && (
          <FormField label={text('navScopeLabel')}>
            <SegmentedControl
              data-testid="stargrid-nav-scope"
              selectedId={scope}
              options={[
                { id: 'mine', text: text('navScopeMine') },
                { id: 'default', text: text('navScopeDefault') }
              ]}
              onChange={({ detail }) => onScopeChange?.(detail.selectedId as NavPreferenceScope)}
            />
          </FormField>
        )}
        <Box variant="p">{text(scope === 'default' ? 'navDefaultHelp' : 'navOrganizeHelp')}</Box>

        {saveError !== undefined && (
          <Alert type="warning" header={text('navSaveFailed')} data-testid="stargrid-nav-save-error">
            {saveError}
          </Alert>
        )}

        <div>
          <Box variant="h4">{text('navSectionOrder')}</Box>
          <div className={styles.sections}>
            {groupOptions.map((section, index) => (
              <div key={section.value} className={styles.sectionRow}>
                <span className={styles.sectionName}>{section.label}</span>
                <SpaceBetween direction="horizontal" size="xxs">
                  <Button
                    variant="icon"
                    iconName="angle-up"
                    disabled={index === 0}
                    ariaLabel={`${text('navMoveSectionUp')}: ${section.label}`}
                    onClick={() => onMoveSection(section.value, -1)}
                  />
                  <Button
                    variant="icon"
                    iconName="angle-down"
                    disabled={index === groupOptions.length - 1}
                    ariaLabel={`${text('navMoveSectionDown')}: ${section.label}`}
                    onClick={() => onMoveSection(section.value, 1)}
                  />
                  {onRemoveGroup && section.value.startsWith(CUSTOM_NAV_GROUP_PREFIX) && (
                    <Button
                      variant="icon"
                      iconName="remove"
                      ariaLabel={`${text('navDeleteSection')}: ${section.label}`}
                      data-testid={`stargrid-nav-delete-section-${section.value}`}
                      onClick={() => onRemoveGroup(section.value)}
                    />
                  )}
                </SpaceBetween>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.newGroup}>
          <div className={styles.newGroupInput}>
            <FormField label={text('navNewSectionName')} description={text('navNewSectionHelp')}>
              <Input
                value={newGroupName}
                placeholder={text('navNewSectionPlaceholder')}
                onChange={({ detail }) => onNewGroupNameChange(detail.value)}
                onKeyDown={({ detail }) => {
                  if (detail.key === 'Enter' && canAddGroup) onAddGroup();
                }}
                data-testid="stargrid-nav-new-section"
              />
            </FormField>
          </div>
          <Button iconName="add-plus" disabled={!canAddGroup} onClick={onAddGroup} data-testid="stargrid-nav-add-section">
            {text('navAddSection')}
          </Button>
        </div>

        <div>
          <Box variant="h4">{text('navReportSections')}</Box>
          {assignments.length === 0 ? (
            <Box variant="p" color="text-body-secondary">
              {text('navOrganizeEmpty')}
            </Box>
          ) : (
            <div className={styles.assignments}>
              {assignments.map(({ report, groupId }) => (
                <div key={report.pnl_report_id} className={styles.assignmentRow}>
                  <span className={styles.reportName} title={report.report_name}>
                    {report.report_name}
                  </span>
                  <Select
                    data-testid={`stargrid-nav-section-${report.pnl_report_id}`}
                    selectedOption={options.find((option) => option.value === groupId) ?? null}
                    options={options}
                    ariaLabel={`${text('navReportSections')}: ${report.report_name}`}
                    onChange={({ detail }) => {
                      const next = detail.selectedOption.value;
                      if (next) onReportGroupChange(report, next);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </SpaceBetween>
    </Modal>
  );
};

export default OrganizeReportsModal;
